import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  ArchiveRestore,
  Briefcase,
  ChevronDown,
  Eye,
  Globe,
  GraduationCap,
  MessageSquare,
  Plus,
  SlidersHorizontal,
  StickyNote,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { auth } from '../../lib/firebase';
import {
  WORKFLOW_STAGES, STAGE_ORDER, resolveCandidateStage, candidateStageLabel,
} from '../../lib/referrerStages';
import ApplicantChatView from '../ApplicantChatView';
import {
  ContextRow,
  ContextSection,
  ConversationList,
  IconButton,
  MessagingAvatar,
  MessagingEmptyState,
  MessagingLayout,
  MessagingTag,
  ThreadFrame,
  compareConversationListOrder,
  formatRelativeTime,
  matchesQuery,
} from '../messaging';
import type { MessagingConversation } from '../messaging';

/**
 * Referrer / agent inbox.
 *
 * Overhaul notes:
 *  • Layout was three fixed-height cards nested three divs deep with
 *    `max-h-[calc(100vh-200px)]` on each, so the panes never lined up and the
 *    context rail overflowed on laptops. It now uses the shared MessagingLayout
 *    (list · thread · context) that fills its container at any size.
 *  • The candidate list had no mobile story — selecting a candidate hid the list
 *    but the thread stayed inside a desktop-proportioned card. Mobile is now a
 *    true single-pane push/pop.
 *  • Search, filters, unread badges, avatars, selection highlight and empty
 *    states come from the design system, so this inbox is pixel-consistent with
 *    the applicant, employer and admin surfaces.
 *  • Team tooling (assignment, tags, private notes, archiving) is preserved
 *    in full and restyled onto the shared context-panel primitives.
 */

interface Candidate {
  id: string;
  userId?: string;
  userUid?: string;
  firstName?: string;
  lastName?: string;
  applicantName?: string;
  programType?: string;
  programName?: string;
  serviceName?: string;
  profilePhotoUrl?: string;
  photoUrl?: string;
  referrerInternalNotes?: string;
  assignedAgentId?: string;
  assignedAgentEmail?: string;
  referrerTags?: string[];
  referrerChatArchived?: boolean;
  chatComments?: { id: string; author: string; text: string; createdAt: string; modified?: boolean }[];
  [key: string]: any;
}

interface Agent {
  uid: string;
  email: string;
  agentRole?: string;
  status?: string;
}

interface Props {
  candidates: Candidate[];
  safeFetch: (url: string, options?: any) => Promise<any>;
  enhancedPermission?: boolean;
  /**
   * Chat §6 contract: opens the referrer's OWN candidate view for the
   * given application ID (ReferrerDashboard's openSlideIn path — the
   * record is resolved fresh by id there, enhanced vs slide-in per the
   * referrer's permission). The inbox passes the ID ONLY — never its
   * possibly-stale list object — and never renders the admin
   * ApplicantPreviewModal.
   */
  onViewApplication?: (applicationId: string) => void;
}

/**
 * §2.2/§4.1: the inbox context tracker uses the SAME canonical 8 workflow
 * stages as every other referrer surface (resolved from the workflow
 * record), replacing this view's private 7-step vocabulary — whose
 * status-string matching almost never matched a real application status,
 * leaving every candidate stuck on "Stage 1 of 7".
 */
const STAGES = WORKFLOW_STAGES.map((s) => s.label);

function candidateName(candidate?: Candidate | null): string {
  if (!candidate) return '';
  return (
    `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() ||
    candidate.applicantName ||
    'Candidate'
  );
}

function candidateProgram(candidate?: Candidate | null): string {
  if (!candidate) return '';
  return candidate.programName || candidate.programType || candidate.serviceName || '';
}

/* ─── Collapsible context section ─────────────────────────────────────────── */

function CollapsibleSection({
  title,
  icon: Icon,
  open,
  onToggle,
  badge,
  children,
}: {
  title: string;
  icon: React.ComponentType<any>;
  open: boolean;
  onToggle: () => void;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="msg-context-section">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="msg-context-label"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          marginBottom: open ? 10 : 0,
          minHeight: 32,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon size={12} />
          {title}
          {badge}
        </span>
        <ChevronDown
          size={14}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }}
        />
      </button>
      {open && children}
    </section>
  );
}

/* ─── Component ───────────────────────────────────────────────────────────── */

export default function ReferrerInboxView({ candidates, safeFetch, enhancedPermission, onViewApplication }: Props) {
  const [allCandidates, setAllCandidates] = useState<Candidate[]>(candidates);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [programFilter, setProgramFilter] = useState('All');
  const [agentFilter, setAgentFilter] = useState('All');
  const [tagFilter, setTagFilter] = useState('All');
  const [boxFilter, setBoxFilter] = useState<'active' | 'unread' | 'archived'>('active');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [newTagInput, setNewTagInput] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [openSections, setOpenSections] = useState({
    assign: true,
    context: true,
    tags: true,
    notes: false,
  });

  useEffect(() => {
    safeFetch('/api/referrer/candidates')
      .then((data: any) => {
        if (data?.candidates) setAllCandidates(data.candidates);
      })
      .catch((err) => console.error('[ReferrerInbox] candidate load failed:', err));

    safeFetch('/api/referrer/agents')
      .then((data: any) => setAgents(data?.agents || []))
      .catch(() => setAgents([]));
  }, [candidates, safeFetch]);

  const selected = useMemo(
    () => allCandidates.find((candidate) => candidate.id === selectedId) || null,
    [allCandidates, selectedId]
  );

  useEffect(() => {
    setNoteDraft('');
    setNewTagInput('');
    setEditingNoteId(null);
  }, [selectedId]);

  const toggleSection = (key: keyof typeof openSections) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ─── Filtering ─────────────────────────────────────────────────────────── */

  const candidatesWithUsers = useMemo(
    () => allCandidates.filter((c) => c.userId || c.userUid || c.id),
    [allCandidates]
  );

  const programs = useMemo(
    () =>
      Array.from(new Set(candidatesWithUsers.map(candidateProgram).filter(Boolean))).sort(),
    [candidatesWithUsers]
  );

  const tags = useMemo(
    () => Array.from(new Set(candidatesWithUsers.flatMap((c) => c.referrerTags || []))).sort(),
    [candidatesWithUsers]
  );

  const filteredCandidates = useMemo(() => {
    const result = candidatesWithUsers.filter((candidate) => {
      const matchesSearch = matchesQuery(
        search,
        candidateName(candidate),
        candidateProgram(candidate),
        candidate.companyName || candidate.employerName,
        candidate.lastMessage,
        ...(candidate.referrerTags || [])
      );
      const matchesProgram = programFilter === 'All' || candidateProgram(candidate) === programFilter;
      const matchesAgent =
        agentFilter === 'All' ||
        (agentFilter === 'Unassigned' ? !candidate.assignedAgentId : candidate.assignedAgentId === agentFilter);
      const matchesTag = tagFilter === 'All' || (candidate.referrerTags || []).includes(tagFilter);
      const matchesBox =
        boxFilter === 'archived'
          ? !!candidate.referrerChatArchived
          : boxFilter === 'unread'
            ? !candidate.referrerChatArchived && (candidate.unreadCount || 0) > 0
            : !candidate.referrerChatArchived;
      return matchesSearch && matchesProgram && matchesAgent && matchesTag && matchesBox;
    });

    // Chat Phase 4: the viewer's pinned conversations sort first
    // (pin is personal — see togglePin; archive here stays the org-level
    // team feature it has always been).
    return result.sort((a, b) =>
      compareConversationListOrder(
        { pinned: !!a.viewerPinned, timestamp: a.lastMessageAt },
        { pinned: !!b.viewerPinned, timestamp: b.lastMessageAt }
      )
    );
  }, [candidatesWithUsers, search, programFilter, agentFilter, tagFilter, boxFilter]);

  const conversations: MessagingConversation[] = useMemo(
    () =>
      filteredCandidates.map((candidate) => ({
        id: candidate.id,
        name: candidateName(candidate),
        subtitle: candidateProgram(candidate) || 'Program not set',
        avatarUrl: candidate.profilePhotoUrl || candidate.photoUrl || '',
        preview: candidate.lastMessage || 'No messages yet',
        timestamp: candidate.lastMessageAt,
        unreadCount: candidate.unreadCount || 0,
        pinned: !!candidate.viewerPinned,
        role: 'applicant',
        status: candidate.status,
        tags: candidate.referrerTags,
        archived: candidate.referrerChatArchived,
        data: candidate,
      })),
    [filteredCandidates]
  );

  const unreadTotal = useMemo(
    () =>
      candidatesWithUsers.filter((c) => !c.referrerChatArchived && (c.unreadCount || 0) > 0).length,
    [candidatesWithUsers]
  );

  /* Chat Phase 4: per-viewer PIN for this inbox. Archive on this
     surface is the pre-existing ORG-level feature (referrerChatArchived,
     shared by the whole referring team — unchanged); pin is personal
     triage, so it rides the per-viewer state endpoint instead. A failed
     write rolls the optimistic flip back. */
  const togglePin = useCallback(
    async (conversation: MessagingConversation) => {
      const candidate = allCandidates.find((c) => c.id === conversation.id);
      if (!candidate) return;
      const nextValue = !candidate.viewerPinned;
      setAllCandidates((prev) =>
        prev.map((c) => (c.id === candidate.id ? { ...c, viewerPinned: nextValue } : c))
      );
      try {
        await safeFetch(`/api/referrer/applications/${candidate.id}/messages/state`, {
          method: 'POST',
          body: JSON.stringify({ pinned: nextValue }),
        });
      } catch {
        setAllCandidates((prev) =>
          prev.map((c) => (c.id === candidate.id ? { ...c, viewerPinned: !nextValue } : c))
        );
      }
    },
    [allCandidates, safeFetch]
  );

  const activeFilterCount =
    (programFilter !== 'All' ? 1 : 0) + (agentFilter !== 'All' ? 1 : 0) + (tagFilter !== 'All' ? 1 : 0);

  /* ─── Metadata mutations ────────────────────────────────────────────────── */

  const saveInboxMeta = useCallback(
    async (updates: Partial<Candidate>) => {
      if (!selected) return;
      setSavingMeta(true);
      try {
        const data = await safeFetch(`/api/referrer/candidates/${selected.id}/inbox`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        });
        const applied = data?.updates || updates;
        setAllCandidates((prev) =>
          prev.map((candidate) =>
            candidate.id === selected.id ? { ...candidate, ...applied } : candidate
          )
        );
      } catch (err) {
        console.error('[ReferrerInbox] failed to save metadata:', err);
      } finally {
        setSavingMeta(false);
      }
    },
    [selected, safeFetch]
  );

  const handleAddTag = async (event: React.FormEvent) => {
    event.preventDefault();
    const tag = newTagInput.trim();
    if (!tag || !selected) return;
    const current = selected.referrerTags || [];
    if (current.includes(tag)) {
      setNewTagInput('');
      return;
    }
    await saveInboxMeta({ referrerTags: [...current, tag] });
    setNewTagInput('');
  };

  const handleRemoveTag = async (tag: string) => {
    if (!selected) return;
    await saveInboxMeta({
      referrerTags: (selected.referrerTags || []).filter((value) => value !== tag),
    });
  };

  const currentUserName = () =>
    auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Referrer';

  const handleAddNote = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = noteDraft.trim();
    if (!text || !selected) return;
    const note = {
      id: `comm_${Math.random().toString(36).slice(2, 11)}`,
      author: currentUserName(),
      text,
      createdAt: new Date().toISOString(),
      modified: false,
    };
    await saveInboxMeta({ chatComments: [...(selected.chatComments || []), note] });
    setNoteDraft('');
  };

  const handleSaveNoteEdit = async (noteId: string) => {
    const text = editingNoteText.trim();
    if (!text || !selected) return;
    await saveInboxMeta({
      chatComments: (selected.chatComments || []).map((note: any) =>
        note.id === noteId
          ? { ...note, text, editedAt: new Date().toISOString(), modified: true }
          : note
      ),
    });
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  /* ─── Panes ─────────────────────────────────────────────────────────────── */

  const filterPanel = filtersOpen ? (
    <div
      style={{
        display: 'grid',
        gap: 8,
        marginTop: 10,
        padding: 10,
        borderRadius: 12,
        border: '1px solid var(--msg-border)',
        background: 'var(--msg-surface-pressed)',
      }}
    >
      <label className="msg-context-label" style={{ margin: 0, display: 'grid', gap: 4 }}>
        Program
        <select
          className="msg-search-input"
          style={{ padding: '8px 10px' }}
          value={programFilter}
          onChange={(event) => setProgramFilter(event.target.value)}
        >
          <option value="All">All programs</option>
          {programs.map((program) => (
            <option key={program} value={program}>
              {program}
            </option>
          ))}
        </select>
      </label>

      {enhancedPermission && (
        <label className="msg-context-label" style={{ margin: 0, display: 'grid', gap: 4 }}>
          Assignee
          <select
            className="msg-search-input"
            style={{ padding: '8px 10px' }}
            value={agentFilter}
            onChange={(event) => setAgentFilter(event.target.value)}
          >
            <option value="All">All assignees</option>
            <option value="Unassigned">Unassigned</option>
            {agents.map((agent) => (
              <option key={agent.uid} value={agent.uid}>
                {agent.email}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="msg-context-label" style={{ margin: 0, display: 'grid', gap: 4 }}>
        Tag
        <select
          className="msg-search-input"
          style={{ padding: '8px 10px' }}
          value={tagFilter}
          onChange={(event) => setTagFilter(event.target.value)}
        >
          <option value="All">All tags</option>
          {tags.map((tag) => (
            <option key={tag} value={tag}>
              #{tag}
            </option>
          ))}
        </select>
      </label>

      {activeFilterCount > 0 && (
        <button
          type="button"
          className="msg-chip"
          onClick={() => {
            setProgramFilter('All');
            setAgentFilter('All');
            setTagFilter('All');
          }}
        >
          <X size={12} />
          Clear {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
        </button>
      )}
    </div>
  ) : null;

  const list = (
    <ConversationList
      title="Inbox"
      subtitle="Candidate conversations for your team"
      conversations={conversations}
      selectedId={selectedId}
      onSelect={(conversation) => setSelectedId(conversation.id)}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search candidates, tags, programs…"
      applySearchFilter={false}
      filters={[
        { id: 'active', label: 'Active' },
        { id: 'unread', label: 'Unread', count: unreadTotal },
        { id: 'archived', label: 'Archived' },
      ]}
      activeFilterId={boxFilter}
      onFilterChange={(id) => setBoxFilter(id as 'active' | 'unread' | 'archived')}
      onTogglePin={(conversation) => void togglePin(conversation)}
      headerActions={
        <IconButton
          label={filtersOpen ? 'Hide filters' : 'Show filters'}
          active={filtersOpen || activeFilterCount > 0}
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <SlidersHorizontal size={16} />
        </IconButton>
      }
      filterPanel={filterPanel}
      emptyTitle="No candidates here"
      emptyDescription={
        boxFilter === 'archived'
          ? 'Archived conversations will show up here.'
          : 'Candidates you refer will appear in this inbox.'
      }
      ariaLabel="Candidate conversations"
    />
  );

  const selectedName = candidateName(selected);
  const selectedProgram = candidateProgram(selected);

  const thread = selected ? (
    <ApplicantChatView
      key={`referrer-${selected.id}`}
      chatType="referrer"
      applicationId={selected.id}
      applicantName={selectedName}
      userId="referrer"
      participant={{ kind: 'referrer', id: 'referrer', applicationId: selected.id }}
      onClose={() => setSelectedId(null)}
      mode="split"
      embedded
      programType={selectedProgram}
      status={selected.status}
      onViewApplication={onViewApplication}
    />
  ) : (
    <ThreadFrame>
      <MessagingEmptyState
        title="Select a candidate"
        description="Choose a candidate from the inbox to read and reply to their messages."
        icon={<MessageSquare size={28} strokeWidth={1.75} />}
      />
    </ThreadFrame>
  );

  const stageIndex = selected ? STAGE_ORDER.indexOf(resolveCandidateStage(selected)) : 0;

  const context = selected ? (
    <>
      <ContextSection label="Candidate">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MessagingAvatar
            name={selectedName}
            photoUrl={selected.profilePhotoUrl || selected.photoUrl}
            size="lg"
          />
          <div style={{ minWidth: 0 }}>
            <p className="msg-item-name" style={{ fontSize: 14 }}>
              {selectedName}
            </p>
            <p className="msg-item-sub" style={{ fontWeight: 500 }}>
              {selectedProgram || 'Program not set'}
            </p>
            {selected.lastMessageAt && (
              <p className="msg-item-sub" style={{ fontWeight: 500 }}>
                Active {formatRelativeTime(selected.lastMessageAt)}
              </p>
            )}
          </div>
        </div>
      </ContextSection>

      {enhancedPermission && (
        <CollapsibleSection
          title="Assign to team member"
          icon={Users}
          open={openSections.assign}
          onToggle={() => toggleSection('assign')}
        >
          <select
            className="msg-search-input"
            value={selected.assignedAgentId || ''}
            disabled={savingMeta}
            onChange={(event) => void saveInboxMeta({ assignedAgentId: event.target.value })}
            aria-label="Assign to team member"
          >
            <option value="">Unassigned</option>
            {agents.map((agent) => (
              <option key={agent.uid} value={agent.uid}>
                {agent.email}
                {agent.agentRole ? ` · ${agent.agentRole}` : ''}
              </option>
            ))}
          </select>
          {!selected.assignedAgentId && (
            <button
              type="button"
              className="msg-btn msg-btn--ghost"
              style={{ width: '100%', marginTop: 8 }}
              disabled={savingMeta}
              onClick={() => {
                const uid = auth.currentUser?.uid;
                if (uid) void saveInboxMeta({ assignedAgentId: uid });
              }}
            >
              Assign to me
            </button>
          )}
        </CollapsibleSection>
      )}

      <CollapsibleSection
        title="Applicant context"
        icon={GraduationCap}
        open={openSections.context}
        onToggle={() => toggleSection('context')}
      >
        <p className="msg-item-name" style={{ fontSize: 13 }}>
          {candidateStageLabel(selected)}
        </p>
        <div
          style={{ display: 'flex', gap: 4, margin: '8px 0 4px' }}
          role="img"
          aria-label={`Stage ${stageIndex + 1} of ${STAGES.length}`}
        >
          {STAGES.map((stage, index) => (
            <span
              key={stage}
              title={stage}
              style={{
                flex: 1,
                height: 5,
                borderRadius: 999,
                background:
                  index <= stageIndex ? 'var(--msg-accent)' : 'var(--msg-surface-pressed)',
              }}
            />
          ))}
        </div>
        <p className="msg-item-sub" style={{ fontWeight: 500 }}>
          Stage {stageIndex + 1} of {STAGES.length}
        </p>
        <div style={{ marginTop: 10 }}>
          {(selected.jobTitle || selected.opportunityTitle) && (
            <ContextRow icon={<Briefcase size={13} />}>
              {selected.jobTitle || selected.opportunityTitle}
            </ContextRow>
          )}
          {(selected.companyName || selected.employerName) && (
            <ContextRow icon={<Globe size={13} />}>
              {selected.companyName || selected.employerName}
            </ContextRow>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Team tags"
        icon={Tag}
        open={openSections.tags}
        onToggle={() => toggleSection('tags')}
        badge={<span style={{ opacity: 0.6 }}>({(selected.referrerTags || []).length})</span>}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {(selected.referrerTags || []).length === 0 && (
            <span className="msg-item-sub" style={{ fontWeight: 500 }}>
              No tags yet.
            </span>
          )}
          {(selected.referrerTags || []).map((tag: string) => (
            <span key={tag} className="msg-chip" style={{ cursor: 'default' }}>
              #{tag}
              <IconButton label={`Remove tag ${tag}`} small onClick={() => void handleRemoveTag(tag)}>
                <X size={11} />
              </IconButton>
            </span>
          ))}
        </div>
        <form onSubmit={handleAddTag} style={{ display: 'flex', gap: 6 }}>
          <input
            className="msg-search-input"
            style={{ padding: '8px 10px' }}
            value={newTagInput}
            placeholder="Add tag…"
            aria-label="Add tag"
            onChange={(event) => setNewTagInput(event.target.value)}
          />
          <button type="submit" className="msg-send" style={{ width: 40, height: 40, minWidth: 40, minHeight: 40 }} aria-label="Add tag">
            <Plus size={16} />
          </button>
        </form>
      </CollapsibleSection>

      <CollapsibleSection
        title="Team notes"
        icon={StickyNote}
        open={openSections.notes}
        onToggle={() => toggleSection('notes')}
        badge={<span style={{ opacity: 0.6 }}>({(selected.chatComments || []).length})</span>}
      >
        <div style={{ display: 'grid', gap: 8, maxHeight: 260, overflowY: 'auto', marginBottom: 10 }}>
          {(selected.chatComments || []).length === 0 && (
            <p className="msg-item-sub" style={{ fontWeight: 500 }}>
              Private notes are only visible to your team.
            </p>
          )}
          {(selected.chatComments || []).map((note: any) => {
            const isOwn = note.author === currentUserName();
            return (
              <div key={note.id} className="msg-bubble msg-bubble--note" style={{ fontSize: 12.5 }}>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontSize: 11,
                    fontWeight: 700,
                    opacity: 0.75,
                  }}
                >
                  <span>{note.author}</span>
                  <span>{formatRelativeTime(note.createdAt)}</span>
                </span>
                {editingNoteId === note.id ? (
                  <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                    <textarea
                      className="msg-search-input"
                      style={{ minHeight: 60, padding: 8 }}
                      value={editingNoteText}
                      onChange={(event) => setEditingNoteText(event.target.value)}
                      aria-label="Edit note"
                    />
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="msg-chip"
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditingNoteText('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="msg-chip msg-chip--active"
                        onClick={() => void handleSaveNoteEdit(note.id)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <p style={{ marginTop: 4 }}>
                    {note.text}
                    {note.modified && <em style={{ opacity: 0.6 }}> · edited</em>}
                  </p>
                )}
                {isOwn && editingNoteId !== note.id && (
                  <button
                    type="button"
                    className="msg-chip"
                    style={{ marginTop: 6, minHeight: 28 }}
                    onClick={() => {
                      setEditingNoteId(note.id);
                      setEditingNoteText(note.text);
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <form onSubmit={handleAddNote} style={{ display: 'flex', gap: 6 }}>
          <input
            className="msg-search-input"
            style={{ padding: '8px 10px' }}
            value={noteDraft}
            placeholder="Add a team note…"
            aria-label="Add a team note"
            onChange={(event) => setNoteDraft(event.target.value)}
          />
          <button type="submit" className="msg-send" style={{ width: 40, height: 40, minWidth: 40, minHeight: 40 }} aria-label="Add note">
            <Plus size={16} />
          </button>
        </form>
      </CollapsibleSection>

      <ContextSection label="Actions">
        <button
          type="button"
          className="msg-btn"
          style={{ width: '100%', marginTop: 0 }}
          onClick={() => onViewApplication?.(selected.id)}
        >
          <Eye size={15} /> View full application
        </button>
        <button
          type="button"
          className="msg-btn msg-btn--ghost"
          style={{ width: '100%' }}
          disabled={savingMeta}
          onClick={() => void saveInboxMeta({ referrerChatArchived: !selected.referrerChatArchived })}
        >
          {selected.referrerChatArchived ? (
            <>
              <ArchiveRestore size={15} /> Restore conversation
            </>
          ) : (
            <>
              <Archive size={15} /> Archive conversation
            </>
          )}
        </button>
        {selected.referrerChatArchived && (
          <p className="msg-item-sub" style={{ fontWeight: 500, marginTop: 8 }}>
            Archived conversations stay searchable under the Archived filter.
          </p>
        )}
        {savingMeta && (
          <p className="msg-item-sub" style={{ fontWeight: 500, marginTop: 8 }}>
            Saving…
          </p>
        )}
      </ContextSection>
    </>
  ) : null;

  return (
    <>
      <MessagingLayout
        list={list}
        thread={thread}
        context={context}
        hasSelection={!!selected}
        selectedConversationId={selectedId}
        style={{ height: '100%' }}
      />
    </>
  );
}

export { MessagingTag as ReferrerInboxTag };
